import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, fmtMoney, fmtPct, fmtRoas } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KPICard, EmptyState, Spinner, SectionTitle, roasTone } from '@/components/common';
import { MetricDialog } from '@/components/MetricDialog';
import { LineChart as LineChartIcon, Plus, ChevronDown, Trash2 } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { cn } from '@/lib/utils';

// Chart config hoisted to module scope so child charts don't re-render from fresh object refs
const CHART_MARGIN = { top: 4, right: 8, left: -12, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const TOOLTIP_STYLE = { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const BAR_RADIUS = [3, 3, 0, 0];
const tickDate = (d) => d.slice(5);

export default function Metrics() {
  const [days, setDays] = useState('7');
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const [d, p] = await Promise.all([api.get(`/metrics/dashboard?days=${days}`), api.get('/products')]);
    setData(d.data);
    setProducts(p.data);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const busy = (data?.entries || []).some((m) => m.diagnosis_status === 'pending');
    clearInterval(pollRef.current);
    if (busy) pollRef.current = setInterval(load, 6000);
    return () => clearInterval(pollRef.current);
  }, [data, load]);

  const delEntry = async (m) => {
    await api.delete(`/metrics/${m.id}`);
    load();
  };

  const t = data?.totals;
  // Newest-first entries, computed once per data change instead of on every render
  const entriesDesc = useMemo(() => [...(data?.entries || [])].reverse(), [data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Metrics / Daily PnL</h1>
          <div className="page-accent-bar" />
          <p className="mt-2 text-sm text-muted-foreground">Manual daily entry from Ads Manager + store — the AI diagnoses every day you log.</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={days} onValueChange={setDays}>
            <TabsList>
              <TabsTrigger data-testid="metrics-range-7" value="7">7d</TabsTrigger>
              <TabsTrigger data-testid="metrics-range-14" value="14">14d</TabsTrigger>
              <TabsTrigger data-testid="metrics-range-30" value="30">30d</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button data-testid="metrics-log-button" onClick={() => setDialogOpen(true)} className="accent-btn gap-2"><Plus className="h-4 w-4" /> Log Metrics</Button>
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <KPICard testId="metrics-kpi-revenue" label="Revenue" value={fmtMoney(t.revenue)} />
            <KPICard testId="metrics-kpi-spend" label="Spend" value={fmtMoney(t.spend)} />
            <KPICard testId="metrics-kpi-profit" label="Profit" value={fmtMoney(t.profit)} tone={t.profit > 0 ? 'good' : t.profit < 0 ? 'bad' : undefined} />
            <KPICard testId="metrics-kpi-roas" label="ROAS" value={fmtRoas(t.roas)} tone={t.roas >= 2 ? 'good' : t.spend > 0 && t.roas < 1 ? 'bad' : undefined} />
            <KPICard testId="metrics-kpi-cpa" label="CPA" value={t.cpa != null ? fmtMoney(t.cpa) : '—'} />
            <KPICard testId="metrics-kpi-aov" label="AOV" value={t.aov != null ? fmtMoney(t.aov) : '—'} />
            <KPICard testId="metrics-kpi-purchases" label="Purchases" value={t.purchases} />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <KPICard testId="metrics-kpi-ctr" label="CTR" value={fmtPct(t.ctr)} sub="target ≥1.5%" />
            <KPICard testId="metrics-kpi-cpc" label="CPC" value={t.cpc != null ? fmtMoney(t.cpc) : '—'} sub="target ≤$1.50" />
            <KPICard testId="metrics-kpi-cpm" label="CPM" value={fmtMoney(t.cpm)} />
            <KPICard testId="metrics-kpi-cvr" label="Conversion" value={fmtPct(t.cvr)} sub="purchases / visitors" />
            <KPICard testId="metrics-kpi-atc" label="ATC Rate" value={fmtPct(t.atc_rate)} sub="target ≥8%" />
            <KPICard testId="metrics-kpi-checkout" label="Checkout → Buy" value={fmtPct(t.purchase_rate)} sub="target ≥25%" />
          </div>

          <Card>
            <CardContent className="p-4">
              <SectionTitle>Revenue vs Spend vs Profit</SectionTitle>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.day_series} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tickFormatter={tickDate} tick={AXIS_TICK} />
                    <YAxis tick={AXIS_TICK} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={LEGEND_STYLE} />
                    <Bar dataKey="revenue" name="Revenue" fill="hsl(var(--chart-1))" radius={BAR_RADIUS} />
                    <Bar dataKey="spend" name="Spend" fill="hsl(var(--chart-3))" radius={BAR_RADIUS} />
                    <Line type="monotone" dataKey="profit" name="Profit" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {(data.winners.length > 0 || data.losers.length > 0) && (
            <div className="grid gap-4 md:grid-cols-2">
              <Card data-testid="metrics-winners-card"><CardContent className="p-4">
                <SectionTitle>Winning creatives</SectionTitle>
                {data.winners.length === 0 ? <p className="text-xs text-muted-foreground">No winners yet.</p> : data.winners.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-b py-1.5 text-sm last:border-0">
                    <span className="truncate">{c.name}</span>
                    <span className="font-mono tabular-nums text-emerald-600 dark:text-emerald-400">{fmtRoas(c.roas)}</span>
                  </div>
                ))}
              </CardContent></Card>
              <Card data-testid="metrics-losers-card"><CardContent className="p-4">
                <SectionTitle>Losing creatives</SectionTitle>
                {data.losers.length === 0 ? <p className="text-xs text-muted-foreground">No losers identified.</p> : data.losers.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-b py-1.5 text-sm last:border-0">
                    <span className="truncate">{c.name}</span>
                    <span className="font-mono tabular-nums text-rose-600 dark:text-rose-400">{fmtRoas(c.roas)}</span>
                  </div>
                ))}
              </CardContent></Card>
            </div>
          )}

          <div>
            <SectionTitle>Logged days</SectionTitle>
            {data.entries.length === 0 ? (
              <EmptyState testId="metrics-empty" icon={LineChartIcon} title="No metrics logged in this range"
                subtitle="Log a day of spend + funnel numbers and the AI will diagnose it automatically."
                action={<Button data-testid="metrics-empty-log-button" onClick={() => setDialogOpen(true)} className="gap-2"><Plus className="h-4 w-4" />Log Metrics</Button>} />
            ) : (
              <Card>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Product</TableHead>
                    <TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">ROAS</TableHead><TableHead className="text-right">Profit</TableHead>
                    <TableHead>AI Diagnosis</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {entriesDesc.map((m) => (
                      <React.Fragment key={m.id}>
                        <TableRow data-testid="metrics-entry-row" className="cursor-pointer" onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                          <TableCell className="font-mono text-sm tabular-nums">{m.date}</TableCell>
                          <TableCell className="text-sm">{m.product_name}</TableCell>
                          <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(m.spend)}</TableCell>
                          <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(m.revenue)}</TableCell>
                          <TableCell className={cn('text-right font-mono text-sm tabular-nums', roasTone(m.computed?.roas, m.spend))}>{fmtRoas(m.computed?.roas)}</TableCell>
                          <TableCell className={cn('text-right font-mono text-sm tabular-nums', m.computed?.profit > 0 ? 'text-emerald-600 dark:text-emerald-400' : m.computed?.profit < 0 ? 'text-rose-600 dark:text-rose-400' : '')}>{fmtMoney(m.computed?.profit)}</TableCell>
                          <TableCell>
                            {m.diagnosis_status === 'pending' ? <span className="flex items-center gap-1 text-xs text-muted-foreground"><Spinner className="h-3 w-3" />running</span>
                              : m.ai_diagnosis ? <span className="flex items-center gap-1 text-xs"><ChevronDown className="h-3 w-3" />{m.ai_diagnosis.overall_health}</span>
                              : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Button data-testid="metrics-entry-delete" variant="ghost" size="icon" aria-label="Delete entry" onClick={() => delEntry(m)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                          </TableCell>
                        </TableRow>
                        {expanded === m.id && m.ai_diagnosis && (
                          <TableRow><TableCell colSpan={8} className="bg-muted/30">
                            <div className="space-y-2 p-2 text-sm">
                              {m.signal && <p className="rounded bg-amber-500/10 p-2 text-xs font-medium text-amber-700 dark:text-amber-400">{m.signal}</p>}
                              <p><span className="font-semibold">{m.ai_diagnosis.overall_health} · {m.ai_diagnosis.kill_or_continue}:</span> {m.ai_diagnosis.summary}</p>
                              <p><span className="font-semibold">Bottleneck:</span> {m.ai_diagnosis.bottleneck}</p>
                              <ul className="list-disc space-y-1 pl-5">
                                {(m.ai_diagnosis.actions || []).map((a) => <li key={a.action}><span className="font-medium">{a.action}</span> — <span className="text-muted-foreground">{a.why}</span></li>)}
                              </ul>
                              {m.ai_diagnosis.missing_data?.length > 0 && <p className="text-xs text-muted-foreground">Missing data: {m.ai_diagnosis.missing_data.join(', ')}</p>}
                            </div>
                          </TableCell></TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        </>
      )}

      <MetricDialog open={dialogOpen} onOpenChange={setDialogOpen} products={products} onSaved={load} />
    </div>
  );
}
